'use client'

import { startTransition, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { useSession } from 'next-auth/react'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { useReleaseUpdate } from '@/hooks/common/useReleaseUpdate'
import { Link, usePathname, useRouter } from '@/i18n/navigation'
import { locales } from '@/i18n/routing'
import { AppIcon } from '@/components/ui/icons'
import { apiFetch } from '@/lib/api-fetch'
import { clearAuthAndReturnHome } from '@/lib/auth/client-logout'
import { prewarmAiSquarePromptMarketClient } from '@/lib/ai-square/client-cache'
import { queryKeys } from '@/lib/query/keys'
import { useShortDramaAccess } from '@/lib/short-drama/access-client'
import { useWorkspaceFloatingActionConfig } from '@/app/[locale]/workspace/components/WorkspaceFloatingActionContext'

const LAST_WORKSPACE_HREF_KEY_PREFIX = 'aimanju:last-workspace-href'
const LAST_WORKSPACE_TYPE_HREF_KEY_PREFIX = 'aimanju:last-workspace-type-href'
const SHORT_DRAMA_ENTRY_HREF = '/workspace?type=short-drama'
const WORKSPACE_HREF_CHANGED_EVENT = 'workspace:last-href-changed'
const WORKSPACE_ROUTE_CHANGE_START_EVENT = 'aimanju:workspace-route-change-start'
const LAST_WORKSPACE_SESSION_HREF_KEY = `${LAST_WORKSPACE_HREF_KEY_PREFIX}:session`
const LAST_WORKSPACE_TYPE_SESSION_HREF_KEY_PREFIX = `${LAST_WORKSPACE_TYPE_HREF_KEY_PREFIX}:session`
const NAV_SLIDE_DELAY_MS = 0
const NAV_IDLE_PREWARM_TIMEOUT_MS = 1600
const STATIC_WORKSPACE_SEGMENTS = new Set(['asset-hub', 'ai-square', 'ai-chat', 'ai-chat-projects'])
const WorkspaceFloatingActionButton = dynamic(
  () => import('@/app/[locale]/workspace/components/WorkspaceFloatingActionButton'),
  { ssr: false },
)
const UpdateNoticeModal = dynamic(() => import('./UpdateNoticeModal'), { ssr: false })
let workspaceAiChatClientPreloadPromise: Promise<unknown> | null = null
let workspaceNavPrewarmStarted = false
let workspaceAiChatWorksPrewarmStarted = false
let workspaceListPrewarmStarted = false

function preloadWorkspaceAiChatClientChunk() {
  if (!workspaceAiChatClientPreloadPromise) {
    workspaceAiChatClientPreloadPromise = import('@/app/[locale]/workspace/ai-chat/WorkspaceAiChatPageClient')
      .catch(() => {
        workspaceAiChatClientPreloadPromise = null
      })
  }
  return workspaceAiChatClientPreloadPromise
}

type NavIdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number
  cancelIdleCallback?: (handle: number) => void
}

function scheduleNavIdleTask(callback: () => void, timeoutMs = NAV_IDLE_PREWARM_TIMEOUT_MS) {
  if (typeof window === 'undefined') return () => undefined
  const idleWindow = window as NavIdleWindow
  if (typeof idleWindow.requestIdleCallback === 'function') {
    const handle = idleWindow.requestIdleCallback(callback, { timeout: timeoutMs })
    return () => idleWindow.cancelIdleCallback?.(handle)
  }
  const timer = window.setTimeout(callback, Math.min(timeoutMs, 350))
  return () => window.clearTimeout(timer)
}

type ProjectKind = 'short-drama' | 'quick-video' | 'image-text' | 'lite-ppt'

function getLastWorkspaceHrefKey(userId: string) {
  return `${LAST_WORKSPACE_HREF_KEY_PREFIX}:${userId}`
}

function getLastWorkspaceTypeHrefKey(userId: string, kind: ProjectKind) {
  return `${LAST_WORKSPACE_TYPE_HREF_KEY_PREFIX}:${userId}:${kind}`
}

function getLastWorkspaceTypeSessionHrefKey(kind: ProjectKind) {
  return `${LAST_WORKSPACE_TYPE_SESSION_HREF_KEY_PREFIX}:${kind}`
}

function getAccountTypeLabel(accountType: string | null | undefined, isAdmin: boolean) {
  if (isAdmin || accountType === 'root_admin') return '管理员'
  if (accountType === 'mobile_api_child') return '手机接口子账号'
  return 'AI 漫剧子账号'
}

function stripLocalePrefix(pathname: string) {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`
  const segments = normalized.split('/').filter(Boolean)
  const firstSegment = segments[0]

  if (firstSegment && locales.includes(firstSegment as (typeof locales)[number])) {
    const stripped = `/${segments.slice(1).join('/')}`
    return stripped === '/' ? '/' : stripped
  }

  return normalized
}

function normalizeWorkspaceHref(href: string) {
  if (!href) return href

  const [pathnamePart, searchPart = ''] = href.split('?')
  const normalizedPathname = stripLocalePrefix(pathnamePart)
  return searchPart ? `${normalizedPathname}?${searchPart}` : normalizedPathname
}

function resolvePersistableWorkspaceHref(href: string | null | undefined) {
  if (!href) return null

  const normalizedHref = normalizeWorkspaceHref(href)
  const [pathnamePart, searchPart = ''] = normalizedHref.split('?')
  const resolved = resolveWorkspaceLocation(pathnamePart, searchPart)
  return resolved.href ? normalizedHref : null
}

function getProjectKindFromWorkspaceHref(href: string | null | undefined): ProjectKind | null {
  if (!href) return null
  const persistableHref = resolvePersistableWorkspaceHref(href)
  if (!persistableHref) return null
  const [pathnamePart, searchPart = ''] = persistableHref.split('?')
  return resolveWorkspaceLocation(pathnamePart, searchPart).kind
}

function resolveWorkspaceLocation(pathname: string, search: string) {
  const internalPathname = stripLocalePrefix(pathname)
  const href = `${internalPathname}${search ? `?${search}` : ''}`
  const segments = internalPathname.split('/').filter(Boolean)
  const workspaceIndex = segments.indexOf('workspace')
  const workspaceSegment = workspaceIndex >= 0 ? segments[workspaceIndex + 1] : null

  if (workspaceSegment === 'image-text') {
    const imageTextProjectId = segments[workspaceIndex + 2] || null
    return {
      projectId: imageTextProjectId,
      href: imageTextProjectId ? href : null,
      kind: imageTextProjectId ? 'image-text' as ProjectKind : null,
    }
  }

  if (workspaceSegment === 'lite-ppt') {
    const litePptProjectId = segments[workspaceIndex + 2] || null
    return {
      projectId: litePptProjectId,
      href: litePptProjectId ? href : null,
      kind: litePptProjectId ? 'lite-ppt' as ProjectKind : null,
    }
  }

  const projectId = workspaceSegment && !STATIC_WORKSPACE_SEGMENTS.has(workspaceSegment) ? workspaceSegment : null
  const kind: ProjectKind | null = projectId ? (segments[workspaceIndex + 2] === 'quick-video' ? 'quick-video' : 'short-drama') : null

  return {
    projectId,
    href: projectId ? href : null,
    kind,
  }
}

interface NavPillProps {
  pillKey: string
  href: string | { pathname: string }
  label: string
  icon: ReactNode
  active?: boolean
  fallbackActive?: boolean
  onRegister?: (key: string, node: HTMLDivElement | null) => void
  onNavigate?: (event: MouseEvent<HTMLElement>, pillKey: string, href: string | { pathname: string }) => void
  onPreload?: (pillKey: string) => void
}

function NavPill({ pillKey, href, label, icon, active = false, fallbackActive = false, onRegister, onNavigate, onPreload }: NavPillProps) {
  const className = [
    'inline-flex h-10 min-w-max shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 text-sm font-semibold tracking-[0.01em] transition-[color,opacity,background-color,border-color,box-shadow] duration-75',
    active
      ? 'text-white'
      : fallbackActive
        ? 'border border-[rgba(153,170,255,0.65)] bg-[linear-gradient(135deg,#3d5cff_0%,#6c74ff_40%,#8d5eff_100%)] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_12px_24px_rgba(69,84,189,0.22)]'
      : 'text-[rgba(44,58,98,0.84)] hover:text-[rgba(21,29,55,0.96)]',
  ].join(' ')

  const content = (
    <>
      <span className="inline-flex shrink-0 items-center">{icon}</span>
      <span className="whitespace-nowrap">{label}</span>
    </>
  )

  return (
    <div ref={(node) => onRegister?.(pillKey, node)} className="relative z-10">
      <Link
        href={href}
        prefetch={false}
        className={className}
        onFocus={() => onPreload?.(pillKey)}
        onMouseEnter={() => onPreload?.(pillKey)}
        onClick={(event) => onNavigate?.(event, pillKey, href)}
      >
        {content}
      </Link>
    </div>
  )
}

export default function Navbar() {
  const { data: session, status } = useSession()
  const { enabled: shortDramaAccessEnabled } = useShortDramaAccess(true)
  const queryClient = useQueryClient()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const locale = useLocale()
  const t = useTranslations('nav')
  const tc = useTranslations('common')
  const isAdmin = (session?.user?.name ?? '').trim().toLowerCase() === 'admin'
  const accountName = session?.user?.name || '账号'
  const accountType = (session?.user as { accountType?: string } | undefined)?.accountType || ''
  const isSingleSignOnSession = Boolean((session?.user as { externalPlatformId?: string | null; externalUserId?: string | null } | undefined)?.externalPlatformId || (session?.user as { externalPlatformId?: string | null; externalUserId?: string | null } | undefined)?.externalUserId)
  const accountTypeText = getAccountTypeLabel(accountType, isAdmin)
  const { currentVersion, update, showModal, dismissCurrentUpdate } = useReleaseUpdate()
  const floatingActionConfig = useWorkspaceFloatingActionConfig()
  const [browserProjectId, setBrowserProjectId] = useState<string | null>(null)
  const [browserProjectKind, setBrowserProjectKind] = useState<ProjectKind | null>(null)
  const [shortDramaWorkspaceHref, setShortDramaWorkspaceHref] = useState<string | null>(null)
  const [pendingPillKey, setPendingPillKey] = useState<string | null>(null)
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const navTrackRef = useRef<HTMLDivElement | null>(null)
  const accountMenuRef = useRef<HTMLDivElement | null>(null)
  const navItemRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const navDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const indicatorFrameRef = useRef<number | null>(null)
  const scrollFrameRef = useRef<number | null>(null)
  const [isIndicatorReady, setIsIndicatorReady] = useState(false)
  const [navIndicatorStyle, setNavIndicatorStyle] = useState<CSSProperties>({
    width: '0px',
    transform: 'translate3d(0, 0, 0)',
    opacity: 0,
  })
  const [scrollY, setScrollY] = useState(0)
  const SCROLL_THRESHOLD = 100

  const normalizedPath = pathname || '/'
  const isHomePage = stripLocalePrefix(normalizedPath) === '/'
  const search = searchParams?.toString() || ''
  const userId = (session?.user as { id?: string } | undefined)?.id ?? null

  useEffect(() => {
    if (typeof window === 'undefined') return

    const readShortDramaWorkspaceHref = () => {
      const typeSessionHref = resolvePersistableWorkspaceHref(window.sessionStorage.getItem(getLastWorkspaceTypeSessionHrefKey('short-drama')))
      if (typeSessionHref) return typeSessionHref

      if (userId) {
        const typeSavedHref = resolvePersistableWorkspaceHref(window.localStorage.getItem(getLastWorkspaceTypeHrefKey(userId, 'short-drama')))
        if (typeSavedHref) return typeSavedHref
      }

      const legacySessionHref = resolvePersistableWorkspaceHref(window.sessionStorage.getItem(LAST_WORKSPACE_SESSION_HREF_KEY))
      if (getProjectKindFromWorkspaceHref(legacySessionHref) === 'short-drama') return legacySessionHref

      if (!userId) return null

      const legacySavedHref = resolvePersistableWorkspaceHref(window.localStorage.getItem(getLastWorkspaceHrefKey(userId)))
      return getProjectKindFromWorkspaceHref(legacySavedHref) === 'short-drama' ? legacySavedHref : null
    }

    const updateShortDramaWorkspaceHref = () => {
      setShortDramaWorkspaceHref(readShortDramaWorkspaceHref())
    }

    updateShortDramaWorkspaceHref()
    window.addEventListener(WORKSPACE_HREF_CHANGED_EVENT, updateShortDramaWorkspaceHref)
    window.addEventListener('storage', updateShortDramaWorkspaceHref)
    return () => {
      window.removeEventListener(WORKSPACE_HREF_CHANGED_EVENT, updateShortDramaWorkspaceHref)
      window.removeEventListener('storage', updateShortDramaWorkspaceHref)
    }
  }, [userId])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const { pathname: rawPathname, search: rawSearch } = window.location
    const resolved = resolveWorkspaceLocation(rawPathname, rawSearch.replace(/^\?/, ''))

    setBrowserProjectId(resolved.projectId)
    setBrowserProjectKind(resolved.kind)
  }, [normalizedPath, search])

  useEffect(() => {
    setAccountMenuOpen(false)
  }, [normalizedPath, search])

  useEffect(() => {
    if (!accountMenuOpen) return

    const handlePointerDown = (event: globalThis.MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (accountMenuRef.current?.contains(target)) return
      setAccountMenuOpen(false)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAccountMenuOpen(false)
    }

    window.addEventListener('mousedown', handlePointerDown)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('mousedown', handlePointerDown)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [accountMenuOpen])

  useEffect(() => {
    setPendingPillKey(null)
    if (navDelayTimerRef.current) {
      clearTimeout(navDelayTimerRef.current)
      navDelayTimerRef.current = null
    }
  }, [normalizedPath, search])

  const isWorkspaceList = normalizedPath === '/workspace'
  const isAssetHub = normalizedPath === '/workspace/asset-hub'
  const isAiChat = normalizedPath === '/workspace/ai-chat'
  const isAiChatProjectDetail = normalizedPath.startsWith('/workspace/ai-chat-projects')
  const isAiSquare = normalizedPath === '/workspace/ai-square'
  const isProfilePage = normalizedPath === '/profile'
  const isWorkspaceRoute = normalizedPath.startsWith('/workspace')
  const isWorkspaceShellRoute = isWorkspaceRoute || isProfilePage
  const isCompactAiChatNav = isWorkspaceShellRoute
  const showHeaderWorkspaceNav = !isWorkspaceShellRoute
  const showBottomWorkspaceNav = isWorkspaceShellRoute
  const routeActivePillKey = isAssetHub ? 'assetHub' : isAiChat ? 'aiChat' : isAiSquare ? 'aiSquare' : isAiChatProjectDetail || browserProjectKind === 'lite-ppt' ? 'workspace' : shortDramaAccessEnabled && browserProjectId && browserProjectKind === 'short-drama' ? 'projectWorkspace' : isWorkspaceList || browserProjectId ? 'workspace' : null
  const activePillKey = pendingPillKey || routeActivePillKey
  type RouterHref = Parameters<typeof router.push>[0]

  const handleNavPillPreload = (pillKey: string) => {
    if (pillKey === 'aiChat') {
      void preloadWorkspaceAiChatClientChunk()
      router.prefetch('/workspace/ai-chat' as RouterHref)
      return
    }
    if (pillKey === 'aiSquare') {
      router.prefetch('/workspace/ai-square' as RouterHref)
      void prewarmAiSquarePromptMarketClient({ thumbnailLimit: 8 })
      return
    }
    if (pillKey === 'workspace') {
      router.prefetch('/workspace' as RouterHref)
    }
  }

  useEffect(() => {
    if (!session || workspaceNavPrewarmStarted) return
    workspaceNavPrewarmStarted = true

    return scheduleNavIdleTask(() => {
      router.prefetch('/workspace' as RouterHref)
      router.prefetch('/workspace/ai-chat' as RouterHref)
      router.prefetch('/workspace/ai-square' as RouterHref)
      if (isAdmin) router.prefetch('/profile' as RouterHref)

      void preloadWorkspaceAiChatClientChunk()
      void prewarmAiSquarePromptMarketClient({ thumbnailLimit: 8 })

      if (!workspaceListPrewarmStarted) {
        workspaceListPrewarmStarted = true
        void queryClient.prefetchQuery({
          queryKey: queryKeys.workspaceProjects.list(1, 7, '', 'short-drama'),
          staleTime: 60_000,
          queryFn: async () => {
            const response = await apiFetch('/api/projects?page=1&pageSize=7&type=short-drama&preview=0')
            if (!response.ok) throw new Error('Failed to prewarm workspace projects')
            return response.json()
          },
        })
      }

      if (!workspaceAiChatWorksPrewarmStarted) {
        workspaceAiChatWorksPrewarmStarted = true
        void queryClient.prefetchQuery({
          queryKey: queryKeys.aiChatWorkspaceProjects.projects(1, 7, '', false),
          staleTime: 60_000,
          queryFn: async () => {
            const response = await apiFetch('/api/workspace/ai-chat-projects?page=1&pageSize=7&preview=0')
            if (!response.ok) throw new Error('Failed to prewarm AI chat works')
            return response.json()
          },
        })
      }
    })
  }, [isAdmin, queryClient, router, session])

  useEffect(() => {
    if (typeof document === 'undefined') return

    document.body.classList.toggle('workspace-route-performance-mode', isWorkspaceShellRoute)
    document.documentElement.classList.toggle('workspace-route-performance-mode', isWorkspaceShellRoute)
    return () => {
      document.body.classList.remove('workspace-route-performance-mode')
      document.documentElement.classList.remove('workspace-route-performance-mode')
    }
  }, [isWorkspaceShellRoute])

  const handleNavPillNavigate = (
    event: MouseEvent<HTMLElement>,
    pillKey: string,
    href: string | { pathname: string },
  ) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }

    if (pillKey === routeActivePillKey || pillKey === pendingPillKey) {
      return
    }

    event.preventDefault()
    setPendingPillKey(pillKey)

    if (navDelayTimerRef.current) {
      clearTimeout(navDelayTimerRef.current)
    }

    navDelayTimerRef.current = setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(WORKSPACE_ROUTE_CHANGE_START_EVENT))
      }
      startTransition(() => {
        router.push(href as RouterHref)
      })
      navDelayTimerRef.current = null
    }, NAV_SLIDE_DELAY_MS)
  }

  useLayoutEffect(() => {
    if (!showHeaderWorkspaceNav) {
      setIsIndicatorReady((prev) => (prev ? false : prev))
      setNavIndicatorStyle((prev) => (prev.opacity === 0 ? prev : { ...prev, opacity: 0 }))
      return
    }

    const updateIndicator = () => {
      const track = navTrackRef.current
      const activeNode = activePillKey ? navItemRefs.current[activePillKey] : null

      if (!track || !activeNode) {
        setIsIndicatorReady((prev) => (prev ? false : prev))
        setNavIndicatorStyle((prev) => (prev.opacity === 0 ? prev : { ...prev, opacity: 0 }))
        return
      }

      const trackRect = track.getBoundingClientRect()
      const activeRect = activeNode.getBoundingClientRect()
      const offsetX = activeRect.left - trackRect.left

      setNavIndicatorStyle({
        width: `${activeRect.width}px`,
        transform: `translate3d(${offsetX}px, 0, 0)`,
        opacity: 1,
      })
      setIsIndicatorReady(true)
    }

    const scheduleIndicatorUpdate = () => {
      if (indicatorFrameRef.current !== null) return
      indicatorFrameRef.current = window.requestAnimationFrame(() => {
        indicatorFrameRef.current = null
        updateIndicator()
      })
    }

    scheduleIndicatorUpdate()
    window.addEventListener('resize', scheduleIndicatorUpdate)
    return () => {
      if (indicatorFrameRef.current !== null) {
        window.cancelAnimationFrame(indicatorFrameRef.current)
        indicatorFrameRef.current = null
      }
      window.removeEventListener('resize', scheduleIndicatorUpdate)
    }
  }, [activePillKey, normalizedPath, browserProjectId, showHeaderWorkspaceNav])

  useEffect(() => {
    return () => {
      if (navDelayTimerRef.current) {
        clearTimeout(navDelayTimerRef.current)
      }
    }
  }, [])

  // Scroll listener for transparent header on home page
  useEffect(() => {
    const commitScrollY = () => {
      scrollFrameRef.current = null
      const nextScrollY = Math.min(Math.max(window.scrollY, 0), SCROLL_THRESHOLD)
      setScrollY((current) => (Math.abs(current - nextScrollY) >= 2 ? nextScrollY : current))
    }
    const handleScroll = () => {
      if (scrollFrameRef.current !== null) return
      scrollFrameRef.current = window.requestAnimationFrame(commitScrollY)
    }

    if (isHomePage) {
      window.addEventListener('scroll', handleScroll, { passive: true })
      handleScroll()
    }

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current)
        scrollFrameRef.current = null
      }
    }
  }, [isHomePage])

  // Calculate header background opacity based on scroll
  const getHeaderBgOpacity = () => {
    if (!isHomePage) return 1
    if (scrollY >= SCROLL_THRESHOLD) return 1
    if (scrollY <= 0) return 0
    return scrollY / SCROLL_THRESHOLD
  }

  const headerBgOpacity = getHeaderBgOpacity()

  // 如果是首页且未登录，完全隐藏 Navbar（首页有自己的 header）
  if (isHomePage && status !== 'authenticated') {
    return null
  }

  return (
    <>
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 pt-0 ${
          isHomePage && headerBgOpacity < 0.1 ? 'bg-transparent border-none shadow-none' : 'glass-nav'
        }`}
        style={{
          backgroundColor: isHomePage
            ? headerBgOpacity < 0.1 ? 'transparent' : `rgba(15, 15, 35, ${headerBgOpacity * 0.85})`
            : undefined,
          borderColor: isHomePage && headerBgOpacity > 0.1 ? 'rgba(255,255,255,0.1)' : undefined,
        }}
      >
        <div className={`mx-auto max-w-7xl pt-0 ${isCompactAiChatNav ? 'px-2 sm:px-3 lg:px-4' : 'px-4 sm:px-6 lg:px-8'}`}>
          <div className={`flex flex-wrap items-center justify-between ${
            isCompactAiChatNav ? 'min-h-10 gap-2 py-1' : 'min-h-16 gap-3 py-2'
          } ${
            isHomePage && headerBgOpacity < 0.1 ? 'text-white' : ''
          }`}>
            <div className={`flex items-center ${isCompactAiChatNav ? 'gap-1.5' : 'gap-3'}`}>
              <Link href={{ pathname: session ? '/workspace' : '/' }} prefetch={false} className={`group flex items-center ${isCompactAiChatNav ? 'gap-1.5' : 'gap-2.5'}`}>
                {isCompactAiChatNav ? null : (
                  <Image
                    src="/logo.webp?v=1"
                    alt={tc('appName')}
                    width={45}
                    height={45}
                    unoptimized
                    className="object-contain transition-transform group-hover:scale-110"
                  />
                )}
                <span className={`${isCompactAiChatNav ? 'hidden sm:inline text-[13px] leading-4' : 'text-lg'} font-semibold bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent`}>
                  向飞讯 AI 创作平台
                </span>
              </Link>
            </div>

            {session && showBottomWorkspaceNav ? (
              <div className="flex min-w-0 flex-1 justify-center">
                <WorkspaceFloatingActionButton
                  placement="header"
                  compact={isCompactAiChatNav}
                  actionConfig={browserProjectId && browserProjectKind === 'short-drama' ? floatingActionConfig : null}
                />
              </div>
            ) : null}

            <div className={`flex items-center ${isCompactAiChatNav ? 'gap-1.5' : 'gap-3'}`}>
              {status === 'loading' ? (
                <div className={`flex items-center ${isCompactAiChatNav ? 'gap-1.5' : 'gap-3'}`}>
                  <div className={`${isCompactAiChatNav ? 'h-5 w-24' : 'h-10 w-32'} rounded-full bg-[var(--glass-bg-muted)] animate-pulse`} />
                  <div className={`${isCompactAiChatNav ? 'h-5 w-16' : 'h-10 w-24'} rounded-full bg-[var(--glass-bg-muted)] animate-pulse`} />
                  <div className={`${isCompactAiChatNav ? 'h-5 w-14' : 'h-10 w-20'} rounded-full bg-[var(--glass-bg-muted)] animate-pulse`} />
                </div>
              ) : session ? (
                <>
                  {showHeaderWorkspaceNav ? (
                    <div
                      ref={navTrackRef}
                      className="nav-pill-track relative flex max-w-[min(100vw-220px,720px)] items-center gap-1 overflow-x-auto overflow-y-hidden rounded-[0.55rem] border border-white/42 bg-[linear-gradient(180deg,rgba(251,253,255,0.46)_0%,rgba(241,246,255,0.34)_100%)] p-1 shadow-[0_16px_42px_rgba(32,46,88,0.10)] backdrop-blur-[14px]"
                    >
                      <span
                        aria-hidden="true"
                        className="workspace-nav-indicator pointer-events-none absolute inset-y-1 left-1 rounded-[0.55rem] border border-[rgba(153,170,255,0.65)] bg-[linear-gradient(135deg,#3d5cff_0%,#6c74ff_40%,#8d5eff_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_12px_24px_rgba(69,84,189,0.28)] transition-[transform,width,opacity] duration-180 ease-[cubic-bezier(0.2,0.9,0.2,1)]"
                        style={navIndicatorStyle}
                      />
                      <NavPill
                        pillKey="workspace"
                        href={{ pathname: '/workspace' }}
                        label={t('workspace')}
                        icon={<AppIcon name="monitor" className="h-4 w-4" />}
                        active={isIndicatorReady && isWorkspaceList}
                        fallbackActive={!isIndicatorReady && isWorkspaceList}
                        onPreload={handleNavPillPreload}
                        onNavigate={handleNavPillNavigate}
                        onRegister={(key, node) => {
                          navItemRefs.current[key] = node
                        }}
                      />
                      {shortDramaAccessEnabled ? (
                        <NavPill
                          pillKey="projectWorkspace"
                          href={shortDramaWorkspaceHref || SHORT_DRAMA_ENTRY_HREF}
                          label={t('projectWorkspace')}
                          icon={<AppIcon name="sparkles" className="h-4 w-4" />}
                          active={isIndicatorReady && browserProjectKind === 'short-drama' && !!browserProjectId}
                          fallbackActive={!isIndicatorReady && browserProjectKind === 'short-drama' && !!browserProjectId}
                          onPreload={handleNavPillPreload}
                          onNavigate={handleNavPillNavigate}
                          onRegister={(key, node) => {
                            navItemRefs.current[key] = node
                          }}
                        />
                      ) : null}
                      <NavPill
                        pillKey="aiChat"
                        href={{ pathname: '/workspace/ai-chat' }}
                        label={t('aiChat')}
                        icon={<AppIcon name="messageCircle" className="h-4 w-4" />}
                        active={isIndicatorReady && isAiChat}
                        fallbackActive={!isIndicatorReady && isAiChat}
                        onPreload={handleNavPillPreload}
                        onNavigate={handleNavPillNavigate}
                        onRegister={(key, node) => {
                          navItemRefs.current[key] = node
                        }}
                      />
                      <NavPill
                        pillKey="aiSquare"
                        href={{ pathname: '/workspace/ai-square' }}
                        label={t('aiSquare')}
                        icon={<AppIcon name="sparkles" className="h-4 w-4" />}
                        active={isIndicatorReady && isAiSquare}
                        fallbackActive={!isIndicatorReady && isAiSquare}
                        onPreload={handleNavPillPreload}
                        onNavigate={handleNavPillNavigate}
                        onRegister={(key, node) => {
                          navItemRefs.current[key] = node
                        }}
                      />
                    </div>
                  ) : null}

                  {isSingleSignOnSession ? null : (
                    <div ref={accountMenuRef} className={`relative flex items-center ${isCompactAiChatNav ? 'gap-1' : 'gap-2'}`}>
                      <button
                        type="button"
                        onClick={() => setAccountMenuOpen((open) => !open)}
                        aria-expanded={accountMenuOpen}
                        className={[
                          isCompactAiChatNav
                            ? 'inline-flex h-7 items-center gap-1 rounded-full px-2 text-xs font-semibold leading-none transition-all duration-200'
                            : 'inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-all duration-200',
                          accountMenuOpen || isProfilePage
                            ? 'bg-white text-[var(--glass-accent-from)] shadow-[0_12px_30px_rgba(66,87,164,0.16)]'
                            : 'text-[var(--glass-text-secondary)] hover:bg-white/60 hover:text-[var(--glass-text-primary)]',
                        ].join(' ')}
                        title="账号"
                      >
                        <AppIcon name="userRoundCog" className={isCompactAiChatNav ? 'h-3.5 w-3.5' : 'h-4.5 w-4.5'} />
                        <span>账号</span>
                        <AppIcon name="chevronDown" className={`${isCompactAiChatNav ? 'h-3 w-3' : 'h-4 w-4'} transition-transform ${accountMenuOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {accountMenuOpen ? (
                        <div className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-2xl border border-white/70 bg-white/95 text-[#111827] shadow-[0_18px_48px_rgba(17,24,39,0.18)] backdrop-blur-xl">
                          <div className="border-b border-[#e5e7eb] px-4 py-3">
                            <div className="truncate text-sm font-semibold">{accountName}</div>
                            <div className="mt-1 truncate text-xs text-[#6b7280]">{accountTypeText}</div>
                          </div>
                          <div className="p-2">
                            {isAdmin ? (
                              <Link
                                href={{ pathname: '/profile' }}
                                prefetch={false}
                                onClick={() => setAccountMenuOpen(false)}
                                className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-[#374151] transition-colors hover:bg-[#f3f4f6]"
                              >
                                <AppIcon name="settingsHexAlt" className="h-4 w-4" />
                                <span>{t('profile')}</span>
                              </Link>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => {
                                setAccountMenuOpen(false)
                                void clearAuthAndReturnHome(locale)
                              }}
                              className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-[#b91c1c] transition-colors hover:bg-[#fef2f2]"
                            >
                              <AppIcon name="logout" className="h-4 w-4" />
                              <span>退出登录</span>
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Link
                    href={{ pathname: '/', query: { auth: 'signin' } }}
                    prefetch={false}
                    className={`text-sm font-medium transition-colors hover:text-white ${
                      isHomePage && headerBgOpacity < 0.1
                        ? 'text-white/80'
                        : 'text-[var(--glass-text-secondary)] hover:text-[var(--glass-text-primary)]'
                    }`}
                  >
                    {t('signin')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
      {update ? (
        <UpdateNoticeModal
          show={showModal}
          currentVersion={currentVersion}
          latestVersion={update.latestVersion}
          releaseUrl={update.releaseUrl}
          releaseName={update.releaseName}
          publishedAt={update.publishedAt}
          onDismiss={dismissCurrentUpdate}
        />
      ) : null}
    </>
  )
}
